import { type MoreProjectsCardList_portfolioProjects$key } from '#app/__generated__/relay/MoreProjectsCardList_portfolioProjects.graphql.js';
import { ProjectSquareCard } from '#app/components/ProjectSquareCard/ProjectSquareCard.js';
import {
  isCoverImage,
  isCoverStyle,
} from '#app/features/project/utils/projectCover.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { getNextItemsInCyclicalList } from '#app/utilities/getNextItemsInCyclicalList.js';
import * as MoreProjectsCardListStyles from './MoreProjectsCardListStyles.js';
import { graphql, useFragment } from '@contra/relay';

type MoreProjectsCardListProps = {
  readonly currProjectId: string;
  readonly firstName: null | string;
  readonly userProfileRef: MoreProjectsCardList_portfolioProjects$key;
};

export const MoreProjectsCardList = ({
  currProjectId,
  firstName,
  userProfileRef,
}: MoreProjectsCardListProps) => {
  const allProjects = useFragment<MoreProjectsCardList_portfolioProjects$key>(
    graphql`
      fragment MoreProjectsCardList_portfolioProjects on UserProfile {
        portfolioProjects {
          edges {
            node {
              id
              slug
              title
              cover {
                ... on PortfolioProjectImageCover {
                  image {
                    animated
                    uid
                  }
                }
                ... on PortfolioProjectTextCover {
                  style
                }
              }
              id
            }
          }
        }
      }
    `,
    userProfileRef,
  );

  const { track } = useAnalytics();

  const projects = allProjects?.portfolioProjects.edges.map(
    ({ node: { cover, id, slug, title } }) => ({ cover, id, slug, title }),
  );

  // firstName is null if user hasn't completed the onboarding flow
  if (!projects || !firstName) return null;

  const maybeNextThreeProjects = getNextItemsInCyclicalList({
    items: projects,
    targetId: currProjectId,
  });

  if (!maybeNextThreeProjects.length) return null;

  return (
    <MoreProjectsCardListStyles.MoreProjectsContainer data-testid="more-projects-card-list">
      <MoreProjectsCardListStyles.MoreProjectsTitle>
        More Projects by {firstName}
      </MoreProjectsCardListStyles.MoreProjectsTitle>
      <MoreProjectsCardListStyles.SquareCardContainer>
        {maybeNextThreeProjects.map(({ cover, id, slug, title }) => {
          const coverImageUid = isCoverImage(cover.image)
            ? cover?.image.uid
            : undefined;
          const coverStyle = isCoverStyle(cover) ? cover.style : undefined;

          return (
            <ProjectSquareCard
              coverImageUid={coverImageUid}
              coverStyle={coverStyle}
              key={id}
              onClick={() => {
                track('link', {
                  action: 'tapped',
                  destination_path: slug,
                  destination_route: 'project',
                  link_name: 'identity_view_more_projects',
                });
              }}
              slug={slug}
              title={title}
            />
          );
        })}
      </MoreProjectsCardListStyles.SquareCardContainer>
    </MoreProjectsCardListStyles.MoreProjectsContainer>
  );
};
